import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { MouseEvent, useEffect, useState } from "react";
import { Badge, Button, Col, Container, Dropdown, Form, ListGroup, Modal, Row, Tab, Table, Tabs } from "react-bootstrap";
import { CompanyType } from "../../models/company";
import { User } from "../../models/user";
import { useAppSelector } from "../../store";
import { queryClient } from "../../utils/http/queryClient";
import { fetchCompanyUsers, fetchUser, patchUser, putCompanyUser, searchUsers } from "../../utils/http/users";

interface CompanyUsersProps {
  company: CompanyType;
}

// Roles shown in the Edit User modal, organized into tabs and, within each tab,
// into subgroups (one per role prefix).
const roleGroups: {
  key: string;
  label: string;
  subgroups: { label: string; roles: string[] }[];
}[] = [
  {
    key: "company-mgmt",
    label: "Company Mgmt",
    subgroups: [
      { label: "Companies", roles: ["companies-view", "companies-add", "companies-change"] },
      { label: "Users", roles: ["users-view", "users-add", "users-change"] },
      { label: "Files", roles: ["files-view", "files-upload", "files-delete"] },
      { label: "Contacts", roles: ["contacts-edit"] },
      { label: "Translations", roles: ["translations"] },
      {
        label: "Entities",
        roles: [
          "entity-mgmt",
          "windmill-trigger-polling",
          "windmill-trigger-normalization",
          "windmill-trigger-finalization",
        ],
      },
    ],
  },
  {
    key: "templates-v3",
    label: "Templates V3",
    subgroups: [{ label: "Templates", roles: ["templates", "templates-feedback"] }],
  },
  {
    key: "admin",
    label: "Admin",
    subgroups: [{ label: "Admin", roles: ["admin-endpoints", "token-copy"] }],
  },
];

type UserType = "company-specific" | "colibry-users" | "colibry-m2m";

const typeOptions: { value: UserType; label: string }[] = [
  { value: "company-specific", label: "Company Specific" },
  { value: "colibry-users", label: "Colibry (Users)" },
  { value: "colibry-m2m", label: "Colibry (M2M)" },
];

// Company names for which Colibry (Users) should also be selected by default
const colibryCompanyNames = ["template", "colibry"];

// Categorize a user based on their company access scope and email domain.
// Colibry users (all-company access) are split into "M2M" (GCP service accounts,
// *.gserviceaccount.com) and "Users" (everyone else with all-company access).
function categorizeUser(user: User): UserType {
  if (user.company_access?.scope !== "all") return "company-specific";
  return user.email.toLowerCase().includes("gserviceaccount.com") ? "colibry-m2m" : "colibry-users";
}

function TypeMultiSelect({ selected, onChange }: { selected: UserType[]; onChange: (value: UserType[]) => void }) {
  const toggle = (value: UserType) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const label =
    selected.length === 0
      ? "- None -"
      : selected.length === 1
      ? typeOptions.find((o) => o.value === selected[0])?.label
      : `${selected.length} selected`;

  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle
        variant="light"
        size="sm"
        style={{
          border: "1px solid #999999",
          backgroundColor: "white",
          width: "100%",
          textAlign: "left",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ padding: "0.5rem" }}>
        {typeOptions.map((option) => (
          <Form.Check
            key={option.value}
            type="checkbox"
            id={`type-${option.value}`}
            label={option.label}
            checked={selected.includes(option.value)}
            onChange={() => toggle(option.value)}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function CompanyUsers({ company }: CompanyUsersProps) {
  const currentUser = useAppSelector((state) => state.user);
  const canEditUsers = currentUser?.roles?.includes("users-change") || false;
  const canAddUsers = currentUser?.roles?.includes("users-add") || false;

  const defaultTypeFilter: UserType[] = colibryCompanyNames.includes(company.name)
    ? ["company-specific", "colibry-users"]
    : ["company-specific"];

  const [typeFilter, setTypeFilter] = useState<UserType[]>(defaultTypeFilter);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [emailFilter, setEmailFilter] = useState<string>("");
  const [debouncedEmailFilter, setDebouncedEmailFilter] = useState<string>("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState<string>("");
  const [newUserDisplayName, setNewUserDisplayName] = useState<string>("");
  const [debouncedNewUserEmail, setDebouncedNewUserEmail] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeRoleTab, setActiveRoleTab] = useState<string>(roleGroups[0].key);

  // Debounce email filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmailFilter(emailFilter);
    }, 500);

    return () => clearTimeout(timer);
  }, [emailFilter]);

  // Debounce the Add-User email input used for the suggestions dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNewUserEmail(newUserEmail);
    }, 300);

    return () => clearTimeout(timer);
  }, [newUserEmail]);

  // Both Colibry (Users) and Colibry (M2M) map to colibry=true on the backend; the
  // Users/M2M distinction is applied on the frontend by email domain. When both a
  // Colibry type and Company Specific are selected we fetch everything (undefined) and
  // filter client-side.
  const needsCompanySpecific = typeFilter.includes("company-specific");
  const needsColibry = typeFilter.includes("colibry-users") || typeFilter.includes("colibry-m2m");
  const colibryParam = needsCompanySpecific === needsColibry ? undefined : needsColibry;

  const { data: users, isLoading } = useQuery({
    queryKey: ["company-users", company.name, typeFilter, statusFilter, debouncedEmailFilter],
    queryFn: ({ signal }) =>
      fetchCompanyUsers({
        companyName: company.name,
        signal,
        colibry: colibryParam,
        active: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
        email: debouncedEmailFilter || undefined,
      }),
    staleTime: 5000,
    // Keep showing previous results (and stay out of the loading branch) while a new
    // filter combination fetches, so the Type dropdown isn't unmounted mid-interaction.
    placeholderData: keepPreviousData,
  });

  const { data: fetchedUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user", selectedUserId],
    queryFn: ({ signal }) => fetchUser({ userId: selectedUserId!, signal }),
    enabled: !!selectedUserId,
    staleTime: 0,
  });

  // Update editedUser when fetchedUser is loaded
  useEffect(() => {
    if (fetchedUser) {
      setEditedUser(fetchedUser);
    }
  }, [fetchedUser]);

  // Search existing users while typing in the Add-User modal, so an already
  // registered user can be attached to this company instead of recreated.
  const { data: userSuggestions } = useQuery({
    queryKey: ["user-search", debouncedNewUserEmail],
    queryFn: ({ signal }) => searchUsers({ email: debouncedNewUserEmail, signal }),
    enabled: addModalOpen && debouncedNewUserEmail.trim().length >= 2,
    staleTime: 5000,
  });

  // Only suggest users not yet linked to any company: unlinked users (company_access
  // is null) or company-specific users with an empty company_ids list. Colibry
  // (all-company) users already have access, so they're excluded.
  const suggestedUsers = (userSuggestions ?? []).filter((user) => {
    const access = user.company_access;
    if (!access) return true;
    if (access.scope !== "specific") return false;
    return access.company_ids.length === 0;
  });

  const { mutate: mutateUpdateUser } = useMutation({
    mutationFn: patchUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-users", company.name] });
      queryClient.invalidateQueries({ queryKey: ["user", selectedUserId] });
      handleCloseModal();
    },
  });

  const { mutate: mutateCreateUser } = useMutation({
    mutationFn: putCompanyUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["company-users", company.name] });
      handleCloseAddModal();
      // Open the edit modal with the newly created user
      setSelectedUserId(data.id);
      setEditModalOpen(true);
    },
  });

  // Keep only users whose type is currently selected (splits Colibry into Users / M2M)
  const filteredUsers = (users ?? []).filter((user) => typeFilter.includes(categorizeUser(user)));

  // Sort users: company-specific first, then Colibry users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (a.company_access?.scope === "specific" && b.company_access?.scope === "all") return -1;
    if (a.company_access?.scope === "all" && b.company_access?.scope === "specific") return 1;
    return 0;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleClearFilters = () => {
    setEmailFilter("");
    setTypeFilter(defaultTypeFilter);
    setStatusFilter("");
  };

  const handleOpenEditModal = (userId: string) => {
    setSelectedUserId(userId);
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedUserId(null);
    setEditedUser(null);
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewUserEmail("");
    setNewUserDisplayName("");
    setDebouncedNewUserEmail("");
    setShowSuggestions(false);
  };

  const handleSelectSuggestion = (user: User) => {
    setNewUserEmail(user.email);
    setNewUserDisplayName(user.display_name || "");
    setShowSuggestions(false);
  };

  const handleCreateUser = () => {
    if (!newUserEmail || !newUserDisplayName) return;

    mutateCreateUser({
      companyNameOrId: company.name,
      email: newUserEmail,
      displayName: newUserDisplayName,
    });
  };

  const handleSaveUser = () => {
    if (!editedUser || !selectedUserId) return;

    const patch: any = {
      email: editedUser.email,
      display_name: editedUser.display_name,
      company_access: editedUser.company_access,
      active: editedUser.active,
      roles: editedUser.roles,
      social: editedUser.social,
    };

    mutateUpdateUser({ userId: selectedUserId, patch });
  };

  const handleRoleToggle = (role: string) => {
    if (!editedUser) return;

    const roles = editedUser.roles.includes(role)
      ? editedUser.roles.filter((r) => r !== role)
      : [...editedUser.roles, role];

    setEditedUser({ ...editedUser, roles });
  };

  // Total selected across all visible role groups, for the x/y indicator.
  const allVisibleRoles = roleGroups.flatMap((g) => g.subgroups.flatMap((sg) => sg.roles));
  const totalSelectedRoles = editedUser ? allVisibleRoles.filter((role) => editedUser.roles.includes(role)).length : 0;

  // Roles shown in the currently active tab.
  const activeRoleGroup = roleGroups.find((g) => g.key === activeRoleTab) ?? roleGroups[0];
  const visibleRoles = activeRoleGroup.subgroups.flatMap((sg) => sg.roles);
  const allVisibleRolesSelected =
    !!editedUser && visibleRoles.every((role) => editedUser.roles.includes(role));

  // Toggle all roles in the active tab on/off at once.
  const handleToggleVisibleRoles = () => {
    if (!editedUser) return;

    const roles = allVisibleRolesSelected
      ? editedUser.roles.filter((r) => !visibleRoles.includes(r))
      : [...new Set([...editedUser.roles, ...visibleRoles])];

    setEditedUser({ ...editedUser, roles });
  };

  const handleCompanyAccessChange = (scope: "all" | "specific" | "unlinked") => {
    if (!editedUser) return;

    if (scope === "unlinked") {
      setEditedUser({
        ...editedUser,
        company_access: null,
      });
    } else if (scope === "all") {
      setEditedUser({
        ...editedUser,
        company_access: { scope: "all" },
      });
    } else {
      // When switching to "specific", include the current company's ID if not already present
      let companyIds: string[] = [];

      if (editedUser.company_access?.scope === "specific") {
        // Keep existing company IDs
        companyIds = editedUser.company_access.company_ids;
      }

      // Add current company ID if not already in the list
      if (company.id && !companyIds.includes(company.id)) {
        companyIds = [...companyIds, company.id];
      }

      setEditedUser({
        ...editedUser,
        company_access: {
          scope: "specific",
          company_ids: companyIds,
        },
      });
    }
  };

  return (
    <Container>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Email</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              placeholder="Search by email..."
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              style={{ border: "1px solid #999999" }}
            />
          </Form.Group>
        </Col>
        <Col md={canAddUsers ? 2 : 3}>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Type</Form.Label>
            <TypeMultiSelect selected={typeFilter} onChange={setTypeFilter} />
          </Form.Group>
        </Col>
        <Col md={canAddUsers ? 2 : 3}>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Status</Form.Label>
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ border: "1px solid #999999" }}
            >
              <option value="">- All statuses -</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2} className="d-flex align-items-end">
          <Button variant="outline-secondary" size="sm" onClick={handleClearFilters} style={{ width: "100%" }}>
            <i className="bi bi-x-circle me-2"></i>
            Clear
          </Button>
        </Col>
        {canAddUsers && (
          <Col md={2} className="d-flex align-items-end">
            <Button variant="primary" size="sm" onClick={handleOpenAddModal} style={{ width: "100%" }}>
              <i className="bi bi-plus-circle me-2"></i>
              Add User
            </Button>
          </Col>
        )}
      </Row>
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : sortedUsers.length === 0 ? (
        <div className="alert alert-info">No users found for this company.</div>
      ) : (
        <Row>
          <Col>
            <Table striped bordered hover>
              <thead>
              <tr>
                {canEditUsers && <th style={{ width: "50px" }}></th>}
                <th>Display Name</th>
                <th>Email</th>
                <th style={{ width: "100px" }}>Type</th>
                <th style={{ width: "100px" }}>Status</th>
                <th>Roles</th>
                <th style={{ width: "120px" }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <tr key={user.id}>
                  {canEditUsers && (
                    <td style={{ textAlign: "center" }}>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleOpenEditModal(user.id)}
                        style={{ padding: 0, color: "#0d6efd" }}
                      >
                        <i className="bi bi-pencil-square" style={{ fontSize: "1.1em" }}></i>
                      </Button>
                    </td>
                  )}
                  <td>{user.display_name || "-"}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.company_access?.scope === "all" && (
                      <Badge bg="info" className="text-white">
                        Colibry
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Badge bg={user.active ? "success" : "secondary"}>{user.active ? "Active" : "Inactive"}</Badge>
                  </td>
                  <td>
                    {user.roles.length > 0 ? (
                      <div>
                        {user.roles.map((role) => (
                          <Badge key={role} bg="primary" className="me-1">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          </Col>
        </Row>
      )}

      {/* Add User Modal */}
      <Modal show={addModalOpen} onHide={handleCloseAddModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" style={{ position: "relative" }}>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={newUserEmail}
                onChange={(e) => {
                  setNewUserEmail(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                // Delay so a suggestion click registers before the list hides
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
                autoFocus
              />
              {showSuggestions && suggestedUsers.length > 0 && (
                <ListGroup
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1056,
                    maxHeight: "240px",
                    overflowY: "auto",
                    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                  }}
                >
                  {suggestedUsers.map((user) => (
                    <ListGroup.Item
                      key={user.id}
                      action
                      // Select before the input's onBlur fires
                      onMouseDown={(e: MouseEvent) => {
                        e.preventDefault();
                        handleSelectSuggestion(user);
                      }}
                    >
                      <div>{user.email}</div>
                      {user.display_name && (
                        <div className="text-muted" style={{ fontSize: "0.85em" }}>
                          {user.display_name}
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter display name"
                value={newUserDisplayName}
                onChange={(e) => setNewUserDisplayName(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateUser} disabled={!newUserEmail || !newUserDisplayName}>
            Create User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={editModalOpen} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingUser ? (
            <div className="text-center py-3">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : editedUser ? (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={editedUser.email}
                      onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editedUser.display_name || ""}
                      onChange={(e) => setEditedUser({ ...editedUser, display_name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Check
                      type="switch"
                      id="active-switch"
                      label={editedUser.active ? "Active" : "Inactive"}
                      checked={editedUser.active}
                      onChange={(e) => setEditedUser({ ...editedUser, active: e.target.checked })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Social Premium</Form.Label>
                    <Form.Check
                      type="switch"
                      id="social-premium-switch"
                      label={editedUser.social.premium ? "Premium" : "Freemium"}
                      checked={editedUser.social.premium}
                      onChange={(e) =>
                        setEditedUser({
                          ...editedUser,
                          social: { ...editedUser.social, premium: e.target.checked },
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Access</Form.Label>
                    <Form.Select
                      value={editedUser.company_access?.scope ?? "unlinked"}
                      onChange={(e) => handleCompanyAccessChange(e.target.value as "all" | "specific" | "unlinked")}
                    >
                      <option value="unlinked">Unlinked</option>
                      <option value="specific">Company Specific</option>
                      <option value="all">All Companies (Colibry)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Roles</Form.Label>
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span className="text-muted" style={{ fontSize: "0.85em" }}>
                      {totalSelectedRoles}/{allVisibleRoles.length} selected
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleToggleVisibleRoles}
                      title={allVisibleRolesSelected ? "Deselect all shown roles" : "Select all shown roles"}
                      style={{ padding: "0.25rem", color: "#0d6efd" }}
                    >
                      <i
                        className={`bi ${allVisibleRolesSelected ? "bi-toggle-on" : "bi-toggle-off"}`}
                        style={{ fontSize: "1.25em" }}
                      ></i>
                    </Button>
                  </div>
                  <Tabs
                    variant="underline"
                    activeKey={activeRoleTab}
                    onSelect={(k) => k && setActiveRoleTab(k)}
                    className="mb-3"
                  >
                  {roleGroups.map((group) => {
                    const groupRoles = group.subgroups.flatMap((sg) => sg.roles);
                    const selectedCount = groupRoles.filter((role) => editedUser.roles.includes(role)).length;
                    return (
                      <Tab
                        key={group.key}
                        eventKey={group.key}
                        title={
                          <span>
                            {group.label}
                            <Badge bg={selectedCount > 0 ? "primary" : "secondary"} className="ms-2">
                              {selectedCount}/{groupRoles.length}
                            </Badge>
                          </span>
                        }
                      >
                        <Row>
                          {group.subgroups.map((subgroup) => (
                            <Col md={4} key={subgroup.label} className="mb-3">
                              <div
                                className="text-muted text-uppercase mb-1"
                                style={{ fontSize: "0.75em", letterSpacing: "0.05em" }}
                              >
                                {subgroup.label}
                              </div>
                              {subgroup.roles.map((role) => (
                                <Form.Check
                                  key={role}
                                  type="checkbox"
                                  id={`role-${role}`}
                                  label={role}
                                  checked={editedUser.roles.includes(role)}
                                  onChange={() => handleRoleToggle(role)}
                                  style={{ fontSize: "0.875em" }}
                                />
                              ))}
                            </Col>
                          ))}
                        </Row>
                      </Tab>
                    );
                  })}
                  </Tabs>
                </div>
              </Form.Group>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>User ID</Form.Label>
                    <Form.Control type="text" value={editedUser.id} disabled readOnly />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Created At</Form.Label>
                    <Form.Control type="text" value={formatDate(editedUser.created_at)} disabled readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Updated At</Form.Label>
                    <Form.Control type="text" value={formatDate(editedUser.updated_at)} disabled readOnly />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveUser} disabled={!editedUser}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CompanyUsers;
